import { CalendarClock, CheckmarkCircleFilled } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { CreditAcquisition } from 'apis/restaurant-api/merchant-admin/credits/types';
import { typographyCss } from 'common/typography';

import Voucher from './icons/Voucher';
import { OfferMonetaryAmount } from './utils';

type SentOfferDetailsProps = {
  timezone: string;
  sentOffer: CreditAcquisition;
};

const ItemWithIcon = styled.div<{ itemColor?: string }>`
  display: flex;
  gap: 8px;

  svg {
    fill: ${({ itemColor }) => itemColor ?? cbSemanticCssVars.textPrimary};
    flex-shrink: 0;
    height: 20px;
    width: 20px;
  }

  & + & {
    margin-block-start: 8px;
  }
`;

const Info = styled.span`
  ${typographyCss.Small}
  display: block;
  margin-block-start: 2px;
  color: ${cbSemanticCssVars.textSecondary};
`;

const SentOfferDetails = ({ timezone, sentOffer }: SentOfferDetailsProps) => {
  const { locale } = useIntl();

  const expiryDate = DateTime.fromSeconds(sentOffer.expiration_date).setZone(timezone);
  const daysLeft = Math.floor(expiryDate.diffNow('days').days);

  return (
    <>
      <ItemWithIcon>
        <Voucher />
        <FormattedMessage
          id="insights.ratings-and-reviews.offers.offer-description"
          values={{
            amountAndUnit: <OfferMonetaryAmount amount={sentOffer.credit_amount} currency={sentOffer.currency} />,
          }}
          tagName="span"
        />
      </ItemWithIcon>
      {sentOffer.claimed_timestamp ? (
        <ItemWithIcon itemColor={colors.lime100}>
          <CheckmarkCircleFilled />
          <FormattedMessage
            id="insights.ratings-and-reviews.offers.offer-claimed"
            values={{
              timestamp: DateTime.fromSeconds(sentOffer.claimed_timestamp)
                .setZone(timezone)
                .toLocaleString(DateTime.DATETIME_SHORT, { locale }),
            }}
            tagName="span"
          />
        </ItemWithIcon>
      ) : (
        <ItemWithIcon>
          <CalendarClock />
          {daysLeft < 0 ? (
            <FormattedMessage
              id="insights.ratings-and-reviews.offers.offer-expired"
              values={{
                timestamp: expiryDate.toLocaleString(DateTime.DATE_SHORT, { locale }),
              }}
              tagName="span"
            />
          ) : (
            <div>
              <FormattedMessage
                id="insights.ratings-and-reviews.offers.offer-sent-on-datetime"
                values={{
                  timestamp: DateTime.fromSeconds(sentOffer.acquiring_timestamp)
                    .setZone(timezone)
                    .toLocaleString(DateTime.DATETIME_SHORT, { locale }),
                }}
                tagName="span"
              />
              <Info>
                <FormattedMessage
                  id="insights.ratings-and-reviews.offers.offer-valid-until"
                  values={{ days: daysLeft }}
                />
              </Info>
            </div>
          )}
        </ItemWithIcon>
      )}
    </>
  );
};

export default SentOfferDetails;
